import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { useAppSelector } from '~redux/hooks';
import { CustomerAccTypeEnum, GoldConversionStatusEnum, GoldTypeEnum, MetalTypeEnum, TransactionActionEnum } from '~utils/enums';

import AffinLogo from '~assets/images/affin-logo-v1.png';
import ActionButton from '~components/atoms/ActionButton';
import { useDispatch } from 'react-redux';
import combinedActions from '~redux/combinedActions';
import { TBuyOrSellMultipleProductsParams } from '~types/feat/gold';
import Decimal from 'decimal.js';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
}

const ReceiptPDF = () => {
    const dispatch = useDispatch();

    const userDetails = useAppSelector((state) => state.auth.userDetails);
    const selectedConversion = useAppSelector(state => state.gold.selectedConversion);

    const productDetails = useAppSelector((state) => state.gold.allProducts);

    const isGold = selectedConversion?.metalType === MetalTypeEnum.Gold;
    const isSilver = selectedConversion?.metalType === MetalTypeEnum.Silver;

    const metalName = isGold ? 'Gold' : isSilver ? 'Silver' : '';
    const metalNameBM = isGold ? 'Emas' : isSilver ? 'Silver' : '';
    
    const currentCustomerDetails = useAppSelector((state) => state.customer.customerDetails);

    const discountList = useAppSelector((state) => state.setting.allDiscounts);

    const [loading, setLoading] = useState(false);
    const [newSummaryTable, setNewSummaryTable] = useState<Array<{ label: string; value: string | number; }>>([]);

    if (!selectedConversion) return null;

    const customerList = useAppSelector(state => state.customer.allCustomers);
    const customer = customerList.find(item => item.idNo === selectedConversion.idNo);

    useEffect(() => {
        if (!selectedConversion?.idNo) return;

        dispatch(
            combinedActions.customerGetAllCustomersAttempt({
                searchQuery: selectedConversion.idNo,
            })
        );
    }, [selectedConversion]);

    const selectedProduct = productDetails?.find(product => product.productName === selectedConversion.targetProductName);
    
    const denomination = Number(selectedProduct?.metalType === MetalTypeEnum.Gold
        ? selectedProduct.physicalGoldWeight ?? 0
        : selectedProduct?.metalType === MetalTypeEnum.Silver
            ? selectedProduct.physicalSilverWeight ?? 0
            : 0
    );

    const conversionQuantity = Number(selectedConversion.amount || 0);
    const serviceCharge = new Decimal(10);
    const conversionFee = new Decimal(selectedConversion.conversionFee || 0);
    const totalConversionFee = conversionFee.plus(serviceCharge);

    const conversionFeePerUnit =
        conversionQuantity > 0
            ? conversionFee.div(conversionQuantity)
            : new Decimal(0);

    const denominationTableData = Array.from(
        { length: conversionQuantity },
        () => ({
            denomination,
            quantity: 1,
            totalDenoGram: denomination,
            conversionFee: conversionFeePerUnit,
        })
    );

    const totalDenoGram = new Decimal(denomination).mul(conversionQuantity);

    const goldDescriptionTableData = (
        selectedConversion.serialNo || []
    ).map(serialNo => ({
        productName: selectedConversion.targetProductName,
        serialNo,
    }));

    const itemsSummary = [
        {
            label: 'Service Charge / Caj Perkhidmatan',
            value: `RM ${serviceCharge.toFixed(2)}`,
        },
        {
            label: 'Total Conversion Fee / Jumlah Yuran Penukaran',
            value: `RM ${totalConversionFee.toFixed(2)}`,
        },
        {
            label: 'Description / Certicard No / Deskripsi / No. Sijil',
            value: '',
        },
    ];

    useEffect(() => {
        const summaryTable = [
            {
                label: 'Transaction Status / Status Transaksi',
                value: GoldConversionStatusEnum[
                    selectedConversion.status
                ],
            },
            {
                label: 'Transaction Date/Time / Tarikh/Masa Transaksi',
                value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
            },
            {
                label: 'Recipient Reference No. / No. Rujukan Penerima',
                value: selectedConversion.conversionId,
            },
            {
                label: 'Branch Name / Nama Cawangan',
                value: selectedConversion.branchName,
            },
            {
                label:
                    'Name of Customer/Company/Firm/Association / Nama Pelanggan/Syarikat/Firma/Persatuan',
                value: selectedConversion.customerName,
            },
            {
                label: 'Customer ID / ID Pelanggan',
                value: selectedConversion.idNo,
            },
            {
                label: 'Type of Account / Jenis Akaun',
                value: customer?.accountType
                    ? CustomerAccTypeEnum[customer?.accountType]
                    : '-',
            },
            {
                label: 'Debit Account / Akaun Debit',
                value: '',
            },
            ...itemsSummary,
        ];

        setNewSummaryTable(summaryTable);
    }, [
        currentCustomerDetails,
        customer,
        selectedConversion,
    ]);

    // const productTableData = selectedConversion.description.map(
    //     product => ({
    //         productName: product.productName,
    //         pricePerGram: product.pricePerGram,
    //     })
    // );

    const customerDetails = {
        name: selectedConversion.customerName,
        idNo: selectedConversion.idNo,
    };
    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
    }: PageProps) => (
        <Page size='A4' style={copy === 'Customer\'s Copy' ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                {
                    copy === 'Customer\'s Copy' &&
                    <Image
                        style={styles.headerLogo}
                        src={AffinLogo}
                    />
                }
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                    <Text style={styles.headerSubtitle}>{subtitle}</Text>
                </View>
                <Text style={styles.headerCopy}>{copy}</Text>
                <View style={styles.divider} />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderDetails = () => {
        return (
            <View style={styles.detailsTable}>
                {newSummaryTable.map((item, index) => (
                    <React.Fragment key={index}>
                        <View style={styles.detailsRow}>
                            <Text style={styles.detailsCell}>
                                {item.label}
                            </Text>

                            <Text style={styles.detailsCell}>
                                {item.label.includes('Item')
                                    ? ''
                                    : `:${item.value}`}
                            </Text>
                        </View>

                        {item.label ===
                            'Debit Account / Akaun Debit' && (
                            <>
                                <View style={styles.detailsRow}>
                                    <Text style={styles.detailsCell}>
                                        Quantity / Conversion Fee / Kuantiti / Yuran Penukaran
                                    </Text>

                                    <Text style={styles.detailsCell}>
                                        :
                                    </Text>
                                </View>

                                {renderQuantityTable()}
                            </>
                        )}

                        {item.label === 'Gold Description / Certicard No / Deskripsi Emas / No. Sijil' && renderGoldDescriptionTable()}
                    </React.Fragment>
                ))}
            </View>
        );
    };

    const renderQuantityTable = () => {
        return (
            <View style={styles.quantityTableContainer}>
                <View style={styles.quantityTable}>
                    <View style={styles.quantityTableRow}>
                        <Text
                            style={[
                                styles.quantityTableHeaderCell,
                                { width: '25%' },
                            ]}
                        >
                            Denomination/{'\n'}
                            Denominasi{'\n'}
                            (gram)
                        </Text>

                        <Text
                            style={[
                                styles.quantityTableHeaderCell,
                                { width: '20%' },
                            ]}
                        >
                            Quantity/{'\n'}
                            Kuantiti
                        </Text>

                        <Text
                            style={[
                                styles.quantityTableHeaderCell,
                                { width: '30%' },
                            ]}
                        >
                            Total Deno in Gram/{'\n'}
                            Jumlah Deno dalam Gram
                        </Text>

                        <Text
                            style={[
                                styles.quantityTableHeaderCell,
                                { width: '25%' },
                            ]}
                        >
                            Conversion{'\n'}
                            Fee / Yuran{'\n'}
                            Penukaran
                        </Text>
                    </View>

                    {denominationTableData.map((item, index) => (
                        <View
                            key={index}
                            style={styles.quantityTableRow}
                        >
                            <Text
                                style={[
                                    styles.quantityTableCell,
                                    { width: '25%' },
                                ]}
                            >
                                {item.denomination.toFixed(3)}
                            </Text>

                            <Text
                                style={[
                                    styles.quantityTableCell,
                                    { width: '20%' },
                                ]}
                            >
                                {item.quantity}
                            </Text>

                            <Text
                                style={[
                                    styles.quantityTableCell,
                                    { width: '30%' },
                                ]}
                            >
                                {item.totalDenoGram.toFixed(3)}
                            </Text>

                            <Text
                                style={[
                                    styles.quantityTableCell,
                                    { width: '25%' },
                                ]}
                            >
                                {item.conversionFee.toFixed(2)}
                            </Text>
                        </View>
                    ))}

                    <View style={styles.quantityTableRow}>
                        <Text
                            style={[
                                styles.quantityTableCell,
                                styles.quantityTableBold,
                                {
                                    width: '45%',
                                },
                            ]}
                        >
                            TOTAL
                        </Text>

                        <Text
                            style={[
                                styles.quantityTableCell,
                                styles.quantityTableBold,
                                {
                                    width: '30%',
                                },
                            ]}
                        >
                            {totalDenoGram.toFixed(3)} gram
                        </Text>

                        <Text
                            style={[
                                styles.quantityTableCell,
                                styles.quantityTableBold,
                                {
                                    width: '25%',
                                },
                            ]}
                        >
                            RM {conversionFee.toFixed(2)}
                        </Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderGoldDescriptionTable = () => {
        return (
            <View style={styles.goldDescriptionTableContainer}>
                <View style={styles.goldDescriptionTable}>
                    <View style={styles.goldDescriptionTableRow}>
                        <Text
                            style={[
                                styles.goldDescriptionHeaderCell,
                                { width: '70%' },
                            ]}
                        >
                            Gold Product / Produk Emas
                        </Text>

                        <Text
                            style={[
                                styles.goldDescriptionHeaderCell,
                                { width: '30%' },
                            ]}
                        >
                            Certicard No. / No. Sijil
                        </Text>
                    </View>

                    {goldDescriptionTableData.map((item, index) => (
                        <View
                            key={index}
                            style={styles.goldDescriptionTableRow}
                        >
                            <Text
                                style={[
                                    styles.goldDescriptionTableCell,
                                    { width: '70%' },
                                ]}
                            >
                                {item.productName}
                            </Text>

                            <Text
                                style={[
                                    styles.goldDescriptionTableCell,
                                    { width: '30%' },
                                ]}
                            >
                                {item.serialNo}
                            </Text>
                        </View>
                    ))}
                </View>
            </View>
        );
    };

    const renderDeclaration = () => {
        return (
            <View>
                <Text style={styles.declarationTextTitle}>DECLARATION / DEKLARASI</Text>
                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>1- I/We authorise the Bank to debit Total Conversion Fee from my/our Current/Savings/Current-i/Savings-i Account to proceed for the transaction.</Text>
                    <Text style={styles.declarationText}>Saya/Kami
                        memberi kuasa kepada pihak Bank untuk mendebitkan Jumlah Yuran Penukaran dari Akaun Semasa/Simpanan/Semasa-i/Simpanan-i saya/kami bagi
                        meneruskan transaksi tersebut.
                    </Text>
                </View>

                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>2- I/We have read, understood and accepted the information as stated in the Product Disclosure Sheet (&quot;PDS&quot;) and Terms & Conditions (&quot;T&C&quot;) of AFFIN Emas
                        Account-i and agreed to abide to its terms and conditions which can be downloaded from AFFIN website at www.affinalways.com.
                    </Text>
                    <Text style={styles.declarationText}>Saya/Kami telah membaca,
                        memahami dan menerima maklumat sepenuhnya seperti yang tertera di dalam Lembaran Pendedahan Produk (&quot;LPP&quot;) dan Terma & Syarat (&quot;T&S&quot;) Akaun AFFIN
                        Emas-i dan bersetuju untuk mematuhi terma dan syaratnya yang boleh dimuat turun daripada laman web AFFIN www.affinalways.com.
                    </Text>
                </View>

                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>3- I/We agree to the price and the Bank to proceed with the transaction once it has been approved. No cancellation/amendment is allowed.</Text>
                    <Text style={styles.declarationText}>Saya/Kami bersetuju
                        dengan harga tersebut dan Bank boleh meneruskan transaksi sebaik sahaja ia diluluskan. Tiada pembatalan/pindaan dibenarkan.
                    </Text>
                </View>

                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationTextBold}>REMINDER: The Customer is reminded to read and understand the terms and conditions of this Deposit of AFFIN Emas Account-i Summary Details,
                        the PDS and T&C of AFFIN Emas Account-i before signing below. In the event there are any terms and conditions in this Deposit of AFFIN Emas
                        Account-i Summary Details, the PDS and T&C of AFFIN Emas Account- that the Customer does not understand, the Customer is hereby advised to
                        seek independent advice and/or discuss further with the Bank’s representative before signing below. PERINGATAN: Pelanggan diingatkan untuk
                        membaca dan memahami terma dan syarat Ringkasan Deposit Akaun AFFIN Emas-i ini, LLP dan T&S Akaun AFFIN Emas-i sebelum menandatangani
                        di bawah. Sekiranya terdapat terma dan syarat dalam Ringkasan Deposit Akaun AFFIN Emas-i ini, LLP dan T&S Akaun AFFIN Emas-i yang tidak
                        difahami oleh Pelanggan, Pelanggan dengan ini dinasihatkan untuk mendapatkan penjelasan dan/atau berbincang lebih lanjut dengan wakil Bank
                        sebelum menandatangani di bawah.</Text>
                </View>
            </View>
        );
    };

    const renderSignature = () => {
        return (
            <View style={styles.signatureContainer}>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='500'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='200'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.name}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.idNo}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderExtraSignature = () => {
        return (
            <View style={styles.extraSignature}>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text style={{ fontFamily: 'Helvetica-Bold' }}>(If applicable) / (Jika Berkenaan)</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='150'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='150'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderOfficeInputs = () => {
        return (
            <View style={styles.bankTable}>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankHeaderCell}>FOR BANK&apos;S USE ONLY / UNTUK KEGUNAAN BANK SAHAJA</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Accepted by (Teller) / Diterima oleh (Juruwang) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Verified and Approved by (Officer/Branch Manager) / Disahkan dah Diluluskan oleh (Pegawai/Pengurus Bank) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>{`Name / Nama : ${userDetails.displayName}`}</Text>
                    <Text style={styles.bankTableCell}>Name / Nama :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                </View>
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);
        // Create the PDF document

        const docTitle = isGold
            ? 'CONVERSION OF AFFIN EMAS ACCOUNT-i TO PHYSICAL GOLD / PENUKARAN AKAUN AFFIN EMAS-i KEPADA EMAS FIZIKAL'
            : isSilver
                ? 'CONVERSION OF SILVER ACCOUNT-i TO PHYSICAL SILVER / PENUKARAN AKAUN AFFIN SILVER ACCOUNT-i KEPADA SILVER FIZIKAL'
                : 'CONVERSION RECEIPT / RESIT PENUKARAN';

        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT DETAILS / RINGKASAN'
                    copy={'Customer\'s Copy'}
                >
                    {renderDetails()}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter>

                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT DETAILS / RINGKASAN'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Print Receipt'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
            // className={SummaryStyles.container}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    goldDescriptionTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    goldTable: {
        width: '60%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    goldTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    goldHeaderCell: {
        width: '70%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldHeaderCellPrice: {
        width: '30%', // Second column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldTableCell: {
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    quantityTableContainer: {
        width: '70%',
        marginBottom: 15,
    },

    quantityTable: {
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
    },

    quantityTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },

    quantityTableHeaderCell: {
        padding: 2,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontFamily: 'Helvetica-Bold',
    },

    quantityTableCell: {
        padding: 2,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },

    quantityTableBold: {
        fontFamily: 'Helvetica-Bold',
    },
    goldDescriptionTableContainer: {
        width: '70%',
        marginBottom: 15,
    },

    goldDescriptionTable: {
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
    },

    goldDescriptionTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },

    goldDescriptionHeaderCell: {
        padding: 2,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontFamily: 'Helvetica-Bold',
    },

    goldDescriptionTableCell: {
        padding: 2,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    declarationContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 15,
    },
    declarationContainer2: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 5,
    },
    declarationTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationTextTitle: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    declarationText: {
        fontSize: 6,
    },
    declarationTextBold: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    signatureContainer: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    signatureCell: {
        width: '50%',
    },
    extraSignatureContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 5,
        marginBottom: 15,
    },
    extraSignature: {
        width: '50%',
    },
    extraSignatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    extraSignatureCell: {
        width: '40%',
        maxLines: 1,
    },
    bankTable: {
        width: '100%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    bankTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    bankHeaderCell: {
        width: '100%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    bankTableCell: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        borderRightWidth: 1,
        borderColor: '#000000',
    },
});

export default ReceiptPDF;
